import axios from "axios";
import { useContext, useEffect, useCallback, useState, useMemo } from "react";
import { Button, Table } from "react-bootstrap";
import DeleteModal from "../components/modals/DeleteModal";
import SuccessModal from "../components/modals/SuccessModal";
import { useNotificationModal } from "../hooks/useNotificationModal";
import { FaDownload, FaEdit, FaEye, FaTrash, FaEraser } from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";

import "../styles/PurchaseOrder.css";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

const emptyFilter = {
  po_number: "",
  supplier_id: "",
  from_date: "",
  to_date: "",
  supplier_address: "",
  total_quantity: "",
  total_styles: "",
  total_cost: "",
  tolerance_limit: "",
  status: "",
  delivery_date: "",
  created_at: "",
};

export default function PurchaseOrder() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userData } = useContext(AuthContext);

  const {
    showSuccessModal,
    successMessage,
    successTitle,
    showErrorModal,
    errorMessage,
    errorTitle,
    showError,
    hideSuccess,
    hideError,
  } = useNotificationModal();

  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [allPurchaseOrders, setAllPurchaseOrders] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [filter, setFilter] = useState({
    po_number: "",
    supplier_id: "",
    from_date: "",
    to_date: "",
    // additional per-column filters
    supplier_address: "",
    total_quantity: "",
    total_styles: "",
    total_cost: "",
    tolerance_limit: "",
    status: "",
    delivery_date: "",
    created_at: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  // Delete modal state
  const [deleteModalInfo, setDeleteModalInfo] = useState({ po_number: null });

  // Fetch purchase orders
  const fetchPurchaseOrders = useCallback(
    async (filters = null) => {
      try {
        const usedFilters = filters || filter;
        const params = { 
            company_code: userData?.company_code,
            po_type: 'direct',
            ...usedFilters }

        const response = await axios.get(
          `${BASE_URL}/api/admin/po/get-purchase-orders`,
          { params }
        );

        const rows = response.data.purchase_orders || [];
        setAllPurchaseOrders(rows);
        setPurchaseOrders(rows);
      } catch (error) {
        console.error("Error fetching purchase orders:", error);
        showError(error.response?.data?.message || error.message, "Error");
      }
    },
    [userData?.company_code, filter, showError]
  );

  // Debounced filter state so typing in filter inputs triggers auto-search without spamming requests
  const [debouncedFilter, setDebouncedFilter] = useState(filter);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedFilter(filter), 300);
    return () => clearTimeout(t);
  }, [filter]);

  // Trigger fetch when debouncedFilter changes
  useEffect(() => {
    if (userData?.company_code) {
      fetchPurchaseOrders(debouncedFilter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedFilter, userData?.company_code]);

  // Client-side filtered view (apply debouncedFilter to allPurchaseOrders)
  const filteredPurchaseOrders = useMemo(() => {
    if (!debouncedFilter) return allPurchaseOrders;
    const f = debouncedFilter;
    return allPurchaseOrders.filter((po) => {
      // PO number
      if (
        f.po_number &&
        !String(po.po_number || "")
          .toLowerCase()
          .includes(String(f.po_number).toLowerCase())
      )
        return false;
      // Supplier id (if set, match exact id)
      if (f.supplier_id && String(po.supplier_id) !== String(f.supplier_id))
        return false;
      // Supplier address
      if (
        f.supplier_address &&
        !String(po.supplier_address || "")
          .toLowerCase()
          .includes(String(f.supplier_address).toLowerCase())
      )
        return false;
      // total quantity
      if (
        f.total_quantity &&
        !String(po.total_quantity || "").includes(String(f.total_quantity))
      )
        return false;
      // total styles
      if (
        f.total_styles &&
        !String(po.total_styles || "").includes(String(f.total_styles))
      )
        return false;
      // total cost
      if (
        f.total_cost &&
        !String(po.total_cost || "").includes(String(f.total_cost))
      )
        return false;
      // tolerance_limit
      if (
        f.tolerance_limit &&
        !String(po.tolerance_limit || "").includes(String(f.tolerance_limit))
      )
        return false;
      // status
      if (
        f.status &&
        String(po.status || "").toLowerCase() !== String(f.status).toLowerCase()
      )
        return false;
      // delivery_date (compare ISO date prefix)
      if (f.delivery_date && po.delivery_date) {
        const poDate = new Date(po.delivery_date).toISOString().split("T")[0];
        if (poDate !== f.delivery_date) return false;
      }
      // created_at
      if (f.created_at && po.created_at) {
        const poDate = new Date(po.created_at).toISOString().split("T")[0];
        if (poDate !== f.created_at) return false;
      }
      return true;
    });
  }, [allPurchaseOrders, debouncedFilter]);

  // Fetch suppliers with useCallback
  const fetchSuppliers = useCallback(async () => {
    try {
      const response = await axios.get(
        `${BASE_URL}/api/admin/suppliers/get-suppliers`,
        {
          params: { company_code: userData?.company_code },
        }
      );

      setSuppliers(response.data.suppliers || []);
    } catch (error) {
      console.error("Error fetching suppliers:", error);
      showError(error.response?.data?.message || error.message, "Error");
    }
  }, [userData?.company_code, showError]);

  // Fetch initial data
  useEffect(() => {
    if (userData?.company_code) {
      fetchSuppliers();
    }
  }, [userData, userData?.company_code, fetchSuppliers]);

  const handleEditPO = (po) => {
    if (po.status === "Approved") {
      showError("You cannot edit an approved purchase order.", "Error");
      return;
    }
    navigate(`/merchandising/po/${po.po_number}/edit`);
  };

  const handleViewDetails = (po_number) => {
    navigate(`/merchandising/po/${po_number}/view`, {
      state: { from: "/merchandising/po" },
    });
  };

  useEffect(() => {
    if (location.state?.from === "/merchandising/po") {
      navigate("/merchandising/po");
    }
  }, [location.state, navigate]);

  const handleDownloadPO = async (po_number) => {
    try {
      setIsLoading(true);

      const response = await axios.get(
        `${BASE_URL}/api/admin/po/download-purchase-order/${po_number}?company_code=${userData?.company_code}`,
        {
          responseType: "blob",
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `PO-${po_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading PDF:", error);
      showError(error.response?.data?.message || error.message, "Error");
    } finally {
      setIsLoading(false);
    }
  };

  // Open DeleteModal for PO
  const handleDelete = (po_number) => {
    setDeleteModalInfo({ po_number });
  };

  // Called after successful delete from DeleteModal
  const handleDeleteConfirmed = () => {
    setDeleteModalInfo({ po_number: null });
    fetchPurchaseOrders();
  };

  // Reset all filters to empty and refetch purchase orders
  const clearFilters = () => {
    setFilter({ ...emptyFilter });
    // also clear debouncedFilter immediately so UI updates without wait
    setDebouncedFilter({ ...emptyFilter });
    // refetch without filters
    fetchPurchaseOrders({ ...emptyFilter });
  };

  return (
    <div className="purchase-order-container">
      <div className="currency-header">
        <div className="cm-header">
          <h1>Purchase Orders</h1>
        </div>
      </div>

      {/* Loading Indicator */}
      {isLoading && (
        <div className="alert alert-info">
          <div className="d-flex align-items-center">
            <div
              className="spinner-border spinner-border-sm me-2"
              role="status"
            >
              <span className="visually-hidden">Loading...</span>
            </div>
            Processing...
          </div>
        </div>
      )}

      {/* Purchase Orders Table */}
      <div className="table-container mt-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5>Purchase Orders ({purchaseOrders.length})</h5>
          <Button
            onClick={() => navigate("/merchandising/po/new?mode=create")}
            disabled={isLoading}
          >
            Create PO
          </Button>
        </div>

        <div className="po-table-scroll">
          <Table striped bordered hover className="po-table">
            <thead>
              <tr>
                <th>PO Number</th>
                <th>Supplier</th>
                <th>Supplier Address</th>
                <th>Total Quantity</th>
                <th>Total Products</th>
                <th>Total Cost</th>
                <th>Tolerance Limit (%)</th>
                <th>Status</th>
                <th>Delivery Date</th>
                <th>Created date</th>
                <th>Actions</th>
              </tr>
              <tr className="filter-row">
                <th>
                  <input
                    type="text"
                    className="table-column-filter"
                    placeholder="PO Number"
                    value={filter.po_number}
                    onChange={(e) =>
                      setFilter({ ...filter, po_number: e.target.value })
                    }
                  />
                </th>
                <th>
                  <select
                    className="table-column-filter"
                    value={filter.supplier_id}
                    onChange={(e) =>
                      setFilter({ ...filter, supplier_id: e.target.value })
                    }
                  >
                    <option value="">All</option>
                    {suppliers.map((s) => (
                      <option key={s.supplier_id} value={s.supplier_id}>
                        {s.supplier_name}
                      </option>
                    ))}
                  </select>
                </th>
                <th>
                  <input
                    type="text"
                    className="table-column-filter"
                    placeholder="Supplier Address"
                    value={filter.supplier_address}
                    onChange={(e) =>
                      setFilter({ ...filter, supplier_address: e.target.value })
                    }
                  />
                </th>
                <th>
                  <input
                    type="text"
                    className="table-column-filter"
                    placeholder="Total Qty"
                    value={filter.total_quantity}
                    onChange={(e) =>
                      setFilter({ ...filter, total_quantity: e.target.value })
                    }
                  />
                </th>
                <th>
                  <input
                    type="text"
                    className="table-column-filter"
                    placeholder="Total Products"
                    value={filter.total_styles}
                    onChange={(e) =>
                      setFilter({ ...filter, total_styles: e.target.value })
                    }
                  />
                </th>
                <th>
                  <input
                    type="text"
                    className="table-column-filter"
                    placeholder="Total Cost"
                    value={filter.total_cost}
                    onChange={(e) =>
                      setFilter({ ...filter, total_cost: e.target.value })
                    }
                  />
                </th>
                <th>
                  <input
                    type="text"
                    className="table-column-filter"
                    placeholder="Tolerance %"
                    value={filter.tolerance_limit}
                    onChange={(e) =>
                      setFilter({ ...filter, tolerance_limit: e.target.value })
                    }
                  />
                </th>
                <th>
                  <select
                    className="table-column-filter"
                    value={filter.status}
                    onChange={(e) =>
                      setFilter({ ...filter, status: e.target.value })
                    }
                  >
                    <option value="">All</option>
                    <option value="Pending">Pending</option>
                    <option value="Approved">Approved</option>
                  </select>
                </th>
                <th>
                  <input
                    type="date"
                    className="table-column-filter"
                    value={filter.delivery_date}
                    onChange={(e) =>
                      setFilter({ ...filter, delivery_date: e.target.value })
                    }
                  />
                </th>
                <th>
                  <input
                    type="date"
                    className="table-column-filter"
                    value={filter.created_at}
                    onChange={(e) =>
                      setFilter({ ...filter, created_at: e.target.value })
                    }
                  />
                </th>
                <th>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <Button
                      variant="outline"
                      className="pf-clear-filters-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        clearFilters();
                      }}
                      title="Clear filters"
                    >
                      <FaEraser />
                    </Button>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredPurchaseOrders.length === 0 ? (
                <tr>
                  <td colSpan="11" className="text-center">
                    {isLoading ? "Loading..." : "No purchase orders found"}
                  </td>
                </tr>
              ) : (
                filteredPurchaseOrders.map((po) => (
                  <tr key={po.po_number}>
                    <td>{po.po_number}</td>
                    <td>{po.supplier_name || "Unknown Supplier"}</td>
                    <td>{po.supplier_address || "N/A"}</td>
                    <td className="cols-with-number">
                      {po.total_quantity || 0}
                    </td>
                    <td className="cols-with-number">{po.total_styles || 0}</td>
                    <td className="cols-with-number">
                      {po.total_cost || "0.00"}
                    </td>
                    <td className="cols-with-number">
                      {po.tolerance_limit !== undefined
                        ? po.tolerance_limit
                        : 0}
                    </td>
                    <td
                      style={{
                        color:
                          po.status === "Pending"
                            ? "#ff9800"
                            : po.status === "Approved"
                            ? "#11882d"
                            : "#50555a",
                        fontWeight: "600",
                      }}
                    >
                      {po.status || "Pending"}
                    </td>
                    <td>{new Date(po.delivery_date).toLocaleDateString()}</td>
                    <td>{new Date(po.created_at).toLocaleDateString()}</td>
                    <td>
                      <FaEye
                        className="action-icon me-2 text-primary"
                        onClick={() => handleViewDetails(po.po_number)}
                        title="View Details"
                        style={{ cursor: "pointer" }}
                      />
                      <FaEdit
                        className={`action-icon me-2 ${
                          po.status === "Approved" ? " disabled" : ""
                        }`}
                        onClick={() => handleEditPO(po)}
                        title={
                          po.status === "Approved"
                            ? "Cannot edit approved PO"
                            : "Edit"
                        }
                        style={{
                          cursor:
                            po.status === "Approved"
                              ? "not-allowed"
                              : "pointer",
                          opacity: po.status === "Approved" ? 0.5 : 1,
                          color: "#007bff",
                        }}
                      />
                      <FaDownload
                        className="action-icon me-2 text-success"
                        onClick={() => handleDownloadPO(po.po_number)}
                        title="Download PDF"
                        style={{ cursor: "pointer" }}
                      />
                      <FaTrash
                        className={`action-icon text-danger${
                          po.status === "Approved" ? " disabled" : ""
                        }`}
                        onClick={() => {
                          if (po.status === "Approved") {
                            showError(
                              "You cannot delete an approved purchase order."
                            );
                          } else {
                            handleDelete(po.po_number);
                          }
                        }}
                        title={
                          po.status === "Approved"
                            ? "Cannot delete approved PO"
                            : "Delete"
                        }
                        style={{
                          cursor:
                            po.status === "Approved"
                              ? "not-allowed"
                              : "pointer",
                          opacity: po.status === "Approved" ? 0.5 : 1,
                        }}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>
      </div>

      {/* DeleteModal for PO */}
      <DeleteModal
        id={deleteModalInfo.po_number}
        show={!!deleteModalInfo.po_number}
        onHide={() => setDeleteModalInfo({ po_number: null })}
        deleteUrl={(id) =>
          id ? `/api/admin/po/delete-purchase-orders/${id}` : ""
        }
        entityLabel="purchase order"
        modalTitle="Delete Purchase Order"
        confirmMessage="Are you sure you want to delete this purchase order? This action cannot be undone."
        onDeleteSuccess={handleDeleteConfirmed}
      />

      {/* Success Modal */}
      <SuccessModal
        show={showSuccessModal}
        onHide={hideSuccess}
        message={successMessage}
        title={successTitle}
        variant="success"
      />

      {/* Error Modal */}
      <SuccessModal
        show={showErrorModal}
        onHide={hideError}
        message={errorMessage}
        title={errorTitle}
        variant="error"
      />
    </div>
  );
}
